import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CarpentryConfiguratorItem } from '../../configurator/applications/carpentry-configurator-item';
import { CarpentryService } from '../../../../services/configurators-2d/applications/carpentry.service';
import { AgoraMimComponent } from 'src/assets/agora-mim-component.zoid.js';
import { QuotationRecord } from 'src/app/models/configurators-2d/applications/carpentry/quotation-record';
import { environment } from 'src/environments/environment';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { logError } from 'src/app/services/helpers/sentry.helper';
declare const AgoraMimComponent: any;

@Component({
  selector: 'ng-carpentry-carpentry-configuration',
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss',
})
export class CarpentryConfigurationComponent extends CarpentryConfiguratorItem implements OnInit {
  loader: boolean = true;
  scrollY: number;
  scroll: any;

  @Input() quotationRecordId: number = undefined;
  @Output() onComplete: EventEmitter<number> = new EventEmitter();
  @Output() resetQuotationRecordId: EventEmitter<boolean> = new EventEmitter();

  protected tunnelNavigationStore = inject(TunnelNavigationStore);
  protected tunnelDataStore = inject(TunnelDataStore);

  selectedQuotationRecordId: number;

  constructor(carpentryService: CarpentryService) {
    super(carpentryService);
  }

  ngOnInit(): void {
    //cas où on fait nouveau projet depuis un projet menuiserie, le quotationRecordId doit être réinitialisé
    if (this.quotationRecordId != undefined && this.configuration.quotationRecords == undefined) {
      this.resetQuotationRecordId.emit(true);
    } else {
      this.selectedQuotationRecordId = this.service.pendingEditQuotationRecordId ?? this.quotationRecordId;
      this.service.pendingEditQuotationRecordId = undefined;
    }

    if (typeof AgoraMimComponent !== 'undefined') {
      const tokenPromise =
        this.selectedQuotationRecordId == undefined
          ? this.service.getInitialQuoteErt()
          : this.service.getEditQuoteErt(this.selectedQuotationRecordId);
      tokenPromise.then(token => {
        const fullParams = {
          env: environment.configurators2d.applications.carpentry.env,
          usecase: this.selectedQuotationRecordId == undefined ? 'quote' : 'edit',
          elciaRequestToken: token,
          onUserCompleted: (quotationRecordId: number) => this.onUserCompleted(quotationRecordId),
          saveScroll: () => this.saveScroll(),
          scrollToLast: () => this.scrollToLast(),
          scrollTop: () => this.scrollTop(),
        };
        AgoraMimComponent(fullParams).render('#elciaContainer');
        this.loader = false;
      });
    }

    const configuratorElements = document.getElementsByClassName('configurator');
    this.scroll = configuratorElements.length > 0 ? configuratorElements : document.getElementsByClassName('tunnel-content');
  }

  scrollTop() {
    const scrollOptions: ScrollToOptions = {
      top: 0,
      left: 0,
      behavior: 'smooth',
    };
    if (window.scrollY > 0) {
      window.scrollTo(scrollOptions);
    } else if (this.scroll?.[0]) {
      this.scroll[0].scrollTo(scrollOptions);
    }
  }

  scrollToLast() {
    const scrollOptions: ScrollToOptions = {
      top: this.scrollY,
      left: 0,
      behavior: 'smooth',
    };
    if (this.scroll?.[0]) {
      this.scroll[0].scrollTo(scrollOptions);
    }
  }

  saveScroll() {
    if (this.scroll?.[0]) {
      this.scrollY = this.scroll[0].scrollTop;
    }
  }

  /** Exécution à la fin de l'iframe */
  async onUserCompleted(recordId: number): Promise<void> {
    this.loader = true;

    try {
      this.configuration.quotationRecords ??= [];

      if (this.selectedQuotationRecordId != null) {
        //cas modification de produit
        if (recordId != null && recordId != this.selectedQuotationRecordId) {
          const selectRecordIndex = this.configuration.quotationRecords.findIndex(
            q => q.externalId === this.selectedQuotationRecordId
          );

          if (selectRecordIndex !== -1) {
            const existingRecord = this.configuration.quotationRecords[selectRecordIndex];
            const quotationRecord = await this.configureProduct(recordId, existingRecord);
            this.configuration.quotationRecords[selectRecordIndex] = quotationRecord;
          }
        }
      } else {
        //cas nouveau produit
        const quotationRecord = await this.configureProduct(recordId);
        this.configuration.quotationRecords.push(quotationRecord);
        this.configuration.quotationRecords.sort((a, b) => {
          //trier par nom de fournisseur et nom de produit
          const supplierNameComparison = a.supplierName.localeCompare(b.supplierName);
          if (supplierNameComparison !== 0) {
            return supplierNameComparison;
          }
          return a.index - b.index;
        });
      }

      //si le rappel de pose n'a jamais été défini, on le définit
      this.configuration.installationReminder ??= true;
      this.configuration.configurationHasChanged = true;

      // En mode tunnel, pousser la configuration dans le store et passer à l'étape suivante (liste d'achat)
      this.tunnelDataStore.updateConfigurationData(CONFIG_CONTEXT.CARPENTRY, this.configuration);
      this.tunnelNavigationStore.nextStep();
    } finally {
      this.loader = false;
    }
  }

  /** Met à jour le titre du projet. */
  async onTitleChange(title: string): Promise<void> {
    try {
      await this.service.updateTitle(title);
    } catch (error) {
      logError(error);
    }
  }

  /**
   * Complète le produit avec les informations d'elcia
   * @param quotationRecordId
   * @param existingRecord
   * @returns
   */
  async configureProduct(quotationRecordId: number, existingRecord?: QuotationRecord): Promise<QuotationRecord> {
    const details = await this.service.getQuotationRecordDetails(quotationRecordId);
    const product = this.service.convertDetailToShoppingCartProduct(details);
    const newRecord = this.service.convertToQuotationRecord(product);
    if (existingRecord != undefined) {
      newRecord.accessories = existingRecord.accessories;
      newRecord.discount = existingRecord.discount;
      newRecord.index = existingRecord.index;
    }
    newRecord.index ??= this.service.getNextSortIndex();
    newRecord.externalId = quotationRecordId;
    return newRecord;
  }

  isNextButtonActive(): boolean {
    return true;
  }

  checkPageChangeCapability(): boolean {
    return true;
  }
}
